import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';

function FreeTrialConfirm() {
  const navigate = useNavigate();
  const { user, isAuthenticated, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const freeFeatures = [
    '2 Active Job Postings',
    '5 Candidate Applications (lifetime)',
    'Basic AI Scoring',
    'Standard Templates',
    'Limited Dashboard Access'
  ];

  async function handleActivateFreeTrial() {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/payment/activate-free-trial', {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setOpenSnackbar(true);

        // Required behavior: Show success popup for a few seconds before updating UI to "Go to Dashboard"
        setTimeout(() => {
          if (response.data.user) {
            setUser(response.data.user);
          }
          setSuccess(true);
          setLoading(false); // Only stop loading after UI update
        }, 3000); // 3 second delay for visibility
      } else {
        setError(response.data.message || 'Failed to activate free trial.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error activating free trial:', err);
      setError(err.response?.data?.message || 'An error occurred while activating your free trial. Please try again.');
      setLoading(false);
    }
  }

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 7 }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%', boxShadow: 3 }}>
          Your free trial is activated. You can now post 2 jobs and process 5 candidates.
        </Alert>
      </Snackbar>
      <Card elevation={3} sx={{ borderRadius: 4, textAlign: 'center', p: 4 }}>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Start Your Free Trial
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You're about to activate your free trial. You can post up to 2 jobs and evaluate 5 candidates at no cost.
          </Typography>
          
          <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2, textAlign: 'left', mb: 4 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Includes:
            </Typography>
            <List dense>
              {freeFeatures.map((feature, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircle fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!success ? (
              <>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleActivateFreeTrial}
                  disabled={loading}
                  sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1.1rem' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Confirm & Start Free Trial'}
                </Button>
                <Button
                  variant="text"
                  size="large"
                  fullWidth
                  onClick={() => navigate('/pricing')}
                  disabled={loading}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => navigate('/admin/dashboard')}
                sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1.1rem' }}
              >
                Go to Dashboard
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

export default FreeTrialConfirm;
